import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Component } from 'react';
import s from './ErrorBoundary.module.css';
/**
 * Render-cycle error catcher.
 *
 * What this catches:
 *   - Throws in `render()` and lifecycle methods of any descendant
 *   - Throws in hooks during the render phase
 *   - Throws in the constructor of any descendant component
 *
 * What this DOES NOT catch (React limitations, not a bug):
 *   - Async errors (promise rejections, setTimeout callbacks, async event
 *     handlers). For data-fetching, TanStack Query already surfaces these
 *     via `query.error`; for one-off `then()` chains, use try/catch.
 *   - Errors in event handlers — those become `window.onerror` events.
 *   - Errors in the boundary's own fallback render — by design, the
 *     fallback is kept boringly simple to avoid this.
 *   - SSR-time errors — we don't SSR.
 *
 * Crucially: this is **pure additive**. No existing code path changes.
 * Without errors → identical to no-boundary. With errors → instead of
 * blank-screening the whole app, the user sees a fallback they can act on.
 */
export class ErrorBoundary extends Component {
    state = { error: null };
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error('[ErrorBoundary]', this.props.level, error, info.componentStack);
        // Future: hook a telemetry sink here (Sentry/Highlight/etc.) without
        // touching call sites.
    }
    reset = () => {
        this.setState({ error: null });
    };
    renderDefaultFallback(error) {
        const isRoot = this.props.level === 'root';
        return (_jsx("div", { className: s.root, role: "alert", "aria-live": "assertive", children: _jsxs("div", { className: s.card, children: [_jsx("div", { className: s.title, children: isRoot ? 'Application crashed' : 'This page crashed' }), _jsx("div", { className: s.msg, children: error.message || 'An unexpected error occurred.' }), _jsx("div", { className: s.hint, children: isRoot
                            ? "Reloading clears the in-memory state; if the issue persists, the team has been notified via the console log."
                            : 'You can try re-rendering the page, or use the sidebar to navigate elsewhere.' }), _jsx("div", { className: s.actions, children: isRoot ? (_jsx("button", { type: "button", className: s.btn, onClick: () => window.location.reload(), children: "Reload" })) : (_jsx("button", { type: "button", className: s.btn, onClick: this.reset, children: "Try again" })) })] }) }));
    }
    render() {
        const { error } = this.state;
        if (error === null)
            return this.props.children;
        if (this.props.fallback)
            return this.props.fallback(error, this.reset);
        return this.renderDefaultFallback(error);
    }
}
